import React, { useState, useEffect } from 'react';
import {
  Search,
  Users,
  MessageSquare,
  Calendar,
  ChevronRight,
  ArrowLeft,
  Settings,
  Pin,
  Heart,
  MessageCircle,
  Share,
  MoreHorizontal,
  UserPlus,
  MapPin,
  Clock,
  Send,
  ThumbsUp
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getSportIcon } from '../../utils/sportUtils';


// Mock data for communities
const mockCommunities = [
  {
    id: '1',
    name: 'Chess Enthusiasts',
    description: 'A passionate community of chess players sharing strategies, games, and tournaments.',
    sport: 'Chess',
    image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=400&h=200&fit=crop',
    coverImage: 'https://images.unsplash.com/photo-1606166187734-a4cb74079037?w=800&h=300&fit=crop',
    members: 1200,
    posts: 450,
    founded: '2023',
    admins: 5,
    coaches: 10,
    isJoined: true,
    rules: [
      'Respect all community members',
      'No spam or promotional content',
      'Share constructively and help others learn'
    ]
  },
  {
    id: '2',
    name: 'Cricket Juniors',
    description: 'Young cricket enthusiasts learning and growing together in the sport.',
    sport: 'Cricket',
    image: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=400&h=200&fit=crop',
    coverImage: 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=800&h=300&fit=crop',
    members: 850,
    posts: 320,
    founded: '2023',
    admins: 3,
    coaches: 8,
    isJoined: false,
    rules: [
      'Maintain sportsmanship at all times',
      'Share training tips and experiences',
      'Support fellow junior players'
    ]
  },
  {
    id: '3',
    name: 'Badminton Champs',
    description: 'Competitive badminton players sharing techniques and tournament updates.',
    sport: 'Badminton',
    image: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=400&h=200&fit=crop',
    coverImage: 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=800&h=300&fit=crop',
    members: 650,
    posts: 280,
    founded: '2024',
    admins: 4,
    coaches: 6,
    isJoined: false,
    rules: [
      'Focus on skill improvement',
      'Share match experiences',
      'Encourage beginners'
    ]
  },
  {
    id: '4',
    name: 'Fitness & Training',
    description: 'General fitness community for athletes across all sports.',
    sport: 'General',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=200&fit=crop',
    coverImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=300&fit=crop',
    members: 2100,
    posts: 890,
    founded: '2022',
    admins: 8,
    coaches: 15,
    isJoined: true,
    rules: [
      'Share workout routines and tips',
      'Support all fitness levels',
      'No body shaming or negativity'
    ]
  }
];

// Mock community posts
const mockCommunityPosts = [
  {
    id: '1',
    communityId: '1',
    isPinned: true,
    user: {
      name: 'Community Manager',
      role: 'Admin',
      sport: 'Chess',
      avatar: null
    },
    content: 'Monthly tournament announcement! Join us for the December Championship on Dec 15th. Registration is now open!',
    image: null,
    timeAgo: '2h',
    likes: 125,
    comments: 20,
    shares: 8,
    isLiked: false
  },
  {
    id: '2',
    communityId: '1',
    isPinned: false,
    user: {
      name: 'Arjun Sharma',
      role: 'Player',
      sport: 'Chess',
      avatar: null
    },
    content: 'Just won my first tournament match! The endgame strategy we discussed really helped. Thanks everyone! 🏆',
    image: 'https://images.unsplash.com/photo-1606166187734-a4cb74079037?w=400&h=300&fit=crop',
    timeAgo: '4h',
    likes: 89,
    comments: 15,
    shares: 5,
    isLiked: true
  }
];

// Mock community events
const mockCommunityEvents = [
  {
    id: '1',
    communityId: '1',
    title: 'National Chess Open',
    description: 'Annual chess championship open to all skill levels',
    sport: 'Chess',
    image: 'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?w=400&h=200&fit=crop',
    date: '2025-09-10T10:47:00Z',
    location: 'Chess Center NYC',
    participants: 45
  },
  {
    id: '2',
    communityId: '1',
    title: 'Weekly Training Session',
    description: 'Regular training for intermediate players',
    sport: 'Chess',
    image: 'https://images.unsplash.com/photo-1606166187734-a4cb74079037?w=400&h=200&fit=crop',
    date: '2025-01-15T18:00:00Z',
    location: 'Community Center',
    participants: 20
  }
];

const CommunityPage: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [communities, setCommunities] = useState(mockCommunities);
  const [selectedCommunity, setSelectedCommunity] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'about' | 'posts' | 'events' | 'people'>('about');
  const [posts, setPosts] = useState(mockCommunityPosts);
  const [events, setEvents] = useState(mockCommunityEvents);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    if (id) {
      loadCommunityDetail(id);
      loadCommunityComments(id);
    } else {
      loadCommunities();
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'about' && id) {
      loadCommunityComments(id);
    }
  }, [activeTab, id]);

  const loadCommunities = async () => {
    try {
      setLoading(true);
      // For now using mock data, uncomment for API integration:
      // const response = await api.get('/communities');
      // setCommunities(response.data.data.communities);
    } catch (error) {
      console.error('Failed to load communities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCommunityDetail = async (communityId: string) => {
    try {
      setLoading(true);
      const community = mockCommunities.find(c => c.id === communityId);
      setSelectedCommunity(community);
      
      // Load community posts and events
      const communityPosts = mockCommunityPosts.filter(p => p.communityId === communityId);
      const communityEvents = mockCommunityEvents.filter(e => e.communityId === communityId);
      setPosts(communityPosts);
      setEvents(communityEvents);
      
      // API calls (uncomment for real implementation):
      // const [communityRes, postsRes, eventsRes] = await Promise.all([
      //   api.get(`/communities/${communityId}`),
      //   api.get(`/communities/${communityId}/posts`),
      //   api.get(`/communities/${communityId}/events`)
      // ]);
      // setSelectedCommunity(communityRes.data.data.community);
      // setPosts(postsRes.data.data.posts);
      // setEvents(eventsRes.data.data.events);
    } catch (error) {
      console.error('Failed to load community detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCommunityComments = async (communityId: string) => {
    try {
      setCommentsLoading(true);
      const response = await api.get(`/comments/community/${communityId}`);
      if (response.data.success && response.data.data) {
        setComments(response.data.data.comments || []);
      }
    } catch (error) {
      console.error('Failed to load community comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddCommunityComment = async () => {
    if (!newComment.trim() || !id) return;

    try {
      setSubmittingComment(true);
      const response = await api.post('/comments', {
        content: newComment.trim(),
        community: id,
      });

      if (response.data.success && response.data.data?.comment) {
        setComments((prev) => [response.data.data.comment, ...prev]);
        setNewComment('');
        toast.success('Comment added successfully!');
      }
    } catch (error: any) {
      console.error('Failed to add comment:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleJoinCommunity = async (communityId: string) => {
    try {
      // Optimistic update
      if (selectedCommunity) {
        setSelectedCommunity((prev: any) => ({
          ...prev,
          isJoined: !prev.isJoined,
          members: prev.isJoined ? prev.members - 1 : prev.members + 1
        }));
      }

      setCommunities((prev: any[]) => prev.map(community => 
        community.id === communityId 
          ? { 
              ...community, 
              isJoined: !community.isJoined,
              members: community.isJoined ? community.members - 1 : community.members + 1
            }
          : community
      ));

      // API call (uncomment for real implementation):
      // await api.post(`/communities/${communityId}/join`);
    } catch (error) {
      console.error('Failed to join/leave community:', error);
    }
  };

  const handleLikePost = async (postId: string) => {
    try {
      setPosts((prev: any[]) => prev.map(post => 
        post.id === postId 
          ? {
              ...post,
              isLiked: !post.isLiked,
              likes: post.isLiked ? post.likes - 1 : post.likes + 1
            }
          : post
      ));

      // API call (uncomment for real implementation):
      // await api.post(`/posts/${postId}/like`);
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-red-100 text-red-800',
      'Cricket': 'bg-red-100 text-red-800',
      'Badminton': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'General': 'bg-gray-100 text-gray-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadgeColor = (role: string) => {
    const colors = {
      'Player': 'bg-blue-100 text-blue-800',
      'Coach': 'bg-green-100 text-green-800',
      'Admin': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Community Discovery View
  if (!id) {
    return (
      <div className="max-w-4xl mx-auto p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Community</h1>
          <button className="p-2 text-gray-600 hover:text-gray-800">
            <Search className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
          <input
            type="text"
            placeholder="Search communities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 sm:pl-10 pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Discover Communities */}
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Discover Communities</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {communities
              .filter(community => 
                community.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                community.sport.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((community) => (
                <div
                  key={community.id}
                  onClick={() => navigate(`/community/${community.id}`)}
                  className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <img
                    src={community.image}
                    alt={community.name}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate flex-1">{community.name}</h3>
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 flex-shrink-0 ml-2" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 line-clamp-2">{community.description}</p>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs sm:text-sm text-gray-500 truncate">
                        {community.members.toLocaleString()} Members | {community.posts} Posts
                      </div>
                      <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getSportBadgeColor(community.sport)} whitespace-nowrap flex-shrink-0`}>
                        {community.sport}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    );
  }

  // Community Detail View
  if (!selectedCommunity) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 bg-white border-b border-gray-200">
        <button
          onClick={() => navigate('/community')}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <h1 className="text-base sm:text-lg font-semibold text-gray-900">Community</h1>
        <button className="p-2 text-gray-600 hover:text-gray-800">
          <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Cover Image */}
      <div className="relative">
        <img
          src={selectedCommunity.coverImage}
          alt={selectedCommunity.name}
          className="w-full h-32 sm:h-48 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Community Info */}
      <div className="bg-white p-3 sm:p-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{selectedCommunity.name}</h2>
            <p className="text-xs sm:text-sm text-gray-600 mb-2">
              {selectedCommunity.members.toLocaleString()} Members | {selectedCommunity.posts} Posts
            </p>
          </div>
          <button
            onClick={() => handleJoinCommunity(selectedCommunity.id)}
            className={`px-4 sm:px-6 py-2 rounded-lg text-sm sm:text-base font-medium transition-colors whitespace-nowrap ${
              selectedCommunity.isJoined
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            {selectedCommunity.isJoined ? 'Joined' : 'Join'}
          </button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center gap-2 sm:space-x-4 text-xs sm:text-sm text-gray-600">
          <span>Founded {selectedCommunity.founded}</span>
          <span className="hidden sm:inline">•</span>
          <span>{selectedCommunity.admins} Admins</span>
          <span className="hidden sm:inline">•</span>
          <span>{selectedCommunity.coaches} Coaches</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <div className="flex space-x-4 sm:space-x-8 px-3 sm:px-4 min-w-max">
          {['about', 'posts', 'events', 'people'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-3 text-xs sm:text-sm font-medium capitalize border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-50 min-h-screen">
        {activeTab === 'about' && (
          <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
            <div className="bg-white rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">About</h3>
              <p className="text-sm sm:text-base text-gray-700">{selectedCommunity.description}</p>
            </div>

            <div className="bg-white rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">Community Rules</h3>
              <ul className="space-y-2">
                {selectedCommunity.rules.map((rule: string, index: number) => (
                  <li key={index} className="flex items-start space-x-2">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-xs sm:text-sm text-gray-700">{rule}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Community Comments Section */}
            <div className="bg-white rounded-lg p-3 sm:p-4">
              <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base flex items-center space-x-2">
                <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Community Discussion</span>
              </h3>
              
              {/* Comment Input */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0">
                    {user ? (user.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || 'U') : 'U'}
                  </div>
                  <div className="flex-1">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Share your thoughts about this community..."
                      className="w-full p-2 sm:p-3 text-sm sm:text-base border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2">
                      <button
                        onClick={handleAddCommunityComment}
                        disabled={!newComment.trim() || submittingComment}
                        className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                      >
                        <Send className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>Comment</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments List */}
              {commentsLoading ? (
                <div className="text-center py-4 text-gray-500 text-sm">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm">No comments yet. Be the first to comment!</div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="border-b border-gray-100 pb-3 sm:pb-4 last:border-0">
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className="relative">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0 overflow-hidden border border-gray-100">
                            {comment.author.avatar ? (
                              <img src={comment.author.avatar} alt={comment.author.name} className="w-full h-full rounded-full object-cover" />
                            ) : (
                              comment.author.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || 'U'
                            )}
                          </div>
                          {comment.author.sport && comment.author.sport !== 'General' && (
                            <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-white w-5 h-5 text-[12px] z-10">
                              {getSportIcon(comment.author.sport)}
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="font-medium text-sm sm:text-base text-gray-900">{comment.author.name}</span>
                            <span className="text-xs text-gray-500">{comment.timeAgo}</span>
                            {comment.edited && (
                              <span className="text-xs text-gray-400">(edited)</span>
                            )}
                          </div>
                          <p className="text-sm sm:text-base text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                          <div className="flex items-center space-x-3 mt-2">
                            <button className="flex items-center space-x-1 text-xs sm:text-sm text-gray-600 hover:text-blue-600">
                              <ThumbsUp className="w-3 h-3 sm:w-4 sm:h-4" />
                              <span>{comment.likes}</span>
                            </button>
                            <button className="text-xs sm:text-sm text-gray-600 hover:text-blue-600">Reply</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'posts' && (
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            {posts.map((post) => (
              <div key={post.id} className="bg-white rounded-lg border border-gray-200">
                {/* Post Header */}
                <div className="p-3 sm:p-4 flex items-start sm:items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                    {post.isPinned && (
                      <Pin className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" />
                    )}
                    <div className="relative">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0 overflow-hidden border border-gray-100">
                        {post.user.avatar ? (
                          <img
                            src={post.user.avatar}
                            alt={post.user.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(post.user.name)
                        )}
                      </div>
                      {post.user.sport && post.user.sport !== 'General' && (
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-white w-5 h-5 text-[12px] z-10">
                          {getSportIcon(post.user.sport)}
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                        <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{post.user.name}</h4>
                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(post.user.role)} whitespace-nowrap`}>
                          {post.user.role}
                        </span>
                        <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(post.user.sport)} whitespace-nowrap`}>
                          {post.user.sport}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{post.timeAgo}</p>
                    </div>
                  </div>
                  <button className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
                    <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>

                {/* Post Content */}
                <div className="px-3 sm:px-4 pb-2 sm:pb-3">
                  <p className="text-sm sm:text-base text-gray-900 break-words">{post.content}</p>
                </div>

                {/* Post Image */}
                {post.image && (
                  <div className="px-3 sm:px-4 pb-2 sm:pb-3">
                    <img
                      src={post.image}
                      alt="Post content"
                      className="w-full h-48 sm:h-64 object-cover rounded-lg"
                    />
                  </div>
                )}

                {/* Post Stats */}
                <div className="px-3 sm:px-4 py-2 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
                    <span>{post.likes} likes</span>
                    <div className="flex space-x-3 sm:space-x-4">
                      <span>{post.comments} comments</span>
                      <span>{post.shares} shares</span>
                    </div>
                  </div>
                </div>

                {/* Post Actions */}
                <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-100">
                  <div className="flex items-center justify-around gap-1">
                    <button
                      onClick={() => handleLikePost(post.id)}
                      className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors flex-1 justify-center ${
                        post.isLiked 
                          ? 'text-red-600 bg-red-50' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${post.isLiked ? 'fill-current' : ''}`} />
                      <span className="text-xs sm:text-sm font-medium hidden sm:inline">Like</span>
                    </button>
                    <button className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center">
                      <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="text-xs sm:text-sm font-medium hidden sm:inline">Comment</span>
                    </button>
                    <button className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center">
                      <Share className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="text-xs sm:text-sm font-medium hidden sm:inline">Share</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'events' && (
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Upcoming Events</h3>
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-lg overflow-hidden border border-gray-200">
                <div className="relative">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-24 sm:h-32 object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getSportBadgeColor(event.sport)}`}>
                      {event.sport}
                    </span>
                  </div>
                </div>
                <div className="p-3 sm:p-4">
                  <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">{event.title}</h4>
                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">{event.description}</p>
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span className="truncate">{event.location}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500">
                      <Users className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                      <span>{event.participants} participants</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'people' && (
          <div className="p-3 sm:p-4">
            <div className="bg-white rounded-lg p-6 sm:p-8 text-center">
              <Users className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Community Members</h3>
              <p className="text-sm sm:text-base text-gray-500">Member directory coming soon</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;